'use client'

import { BirhausToastData, ToastVariant } from '../components/BirhausToast'

/**
 * BIRHAUS Imperative Toast API
 * 
 * Global toast instance that works without provider context.
 * Automatically creates a toast container and manages state.
 * Perfect for quick prototyping and one-off notifications.
 * 
 * Usage:
 * ```ts
 * import { toast } from '@birhaus/primitives'
 * 
 * toast.success('Guardado exitosamente')
 * toast.error('Error al procesar')
 * toast.warning('Revisa los datos')
 * toast.info('Nueva información disponible')
 * ```
 */

export interface ToastOptions {
  title?: string
  titleEs?: string
  titleEn?: string
  message?: string
  messageEs?: string
  messageEn?: string
  duration?: number
  persistent?: boolean
  undoAction?: () => void | Promise<void>
  undoLabel?: string
  undoLabelEs?: string
  undoLabelEn?: string
  locale?: 'es' | 'en' | string
  action?: {
    label: string
    labelEs?: string
    labelEn?: string
    onClick: () => void
  }
}

class BirhausImperativeToast {
  private container: HTMLDivElement | null = null
  private toasts: Map<string, HTMLDivElement> = new Map()
  private maxToasts = 3 // BIRHAUS: Cognitive load limit

  private ensureContainer(): HTMLDivElement {
    if (!this.container) {
      this.container = document.createElement('div')
      this.container.className = 'fixed top-4 right-4 z-50 pointer-events-none space-y-2'
      this.container.setAttribute('aria-live', 'polite')
      this.container.setAttribute('aria-label', 'Notificaciones')
      document.body.appendChild(this.container)
    }
    return this.container
  }

  private createToastElement(variant: ToastVariant, options: ToastOptions): string {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const locale = options.locale || 'es'
    
    // BIRHAUS: Spanish-first messages
    const title = options.titleEs || options.title || ''
    const message = options.messageEs || options.message || ''
    
    // Variant styles (matching BirhausToast component)
    const variantStyles = {
      success: 'bg-green-50 border-green-200 text-green-800',
      error: 'bg-red-50 border-red-200 text-red-800', 
      warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      info: 'bg-blue-50 border-blue-200 text-blue-800'
    }

    const variantIcons = {
      success: '✅',
      error: '❌', 
      warning: '⚠️',
      info: 'ℹ️'
    }

    const container = this.ensureContainer()
    const toastElement = document.createElement('div')
    toastElement.id = id
    toastElement.className = `
      w-80 max-w-sm pointer-events-auto transform transition-all duration-300 ease-out
      bg-white border rounded-lg shadow-lg p-4 ${variantStyles[variant]}
    `.replace(/\s+/g, ' ').trim()

    // Auto dismiss logic
    const duration = options.duration ?? (options.persistent ? null : 5000)
    let dismissTimer: NodeJS.Timeout | null = null

    const dismiss = () => {
      if (dismissTimer) clearTimeout(dismissTimer)
      toastElement.style.transform = 'translateX(100%)'
      toastElement.style.opacity = '0'
      setTimeout(() => {
        if (toastElement.parentNode) {
          toastElement.parentNode.removeChild(toastElement)
        }
        this.toasts.delete(id)
      }, 300)
    }

    // Build content
    let content = `
      <div class="flex items-start gap-3">
        <span class="text-lg flex-shrink-0 mt-0.5">${variantIcons[variant]}</span>
        <div class="flex-1 min-w-0">
    `

    if (title) {
      content += `<div class="font-semibold text-sm">${title}</div>`
    }
    if (message) {
      content += `<div class="text-sm ${title ? 'mt-1' : ''}">${message}</div>`
    }

    // Add action button if provided
    if (options.action) {
      const actionLabel = options.action.labelEs || options.action.label
      content += `
        <button 
          data-action="custom" 
          class="mt-2 text-xs font-medium underline hover:no-underline"
        >
          ${actionLabel}
        </button>
      `
    }

    // Add undo button if provided
    if (options.undoAction) {
      const undoLabel = options.undoLabelEs || options.undoLabel || 'Deshacer'
      content += `
        <button 
          data-action="undo" 
          class="mt-2 text-xs font-medium underline hover:no-underline"
        >
          ${undoLabel}
        </button>
      `
    }

    content += `
        </div>
        <button 
          data-action="dismiss" 
          class="flex-shrink-0 text-gray-400 hover:text-gray-600"
          aria-label="Cerrar"
        >
          ✕
        </button>
      </div>
    `

    toastElement.innerHTML = content

    // Event listeners
    toastElement.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      const action = target.getAttribute('data-action')
      
      switch (action) {
        case 'dismiss':
          dismiss()
          break
        case 'undo':
          if (options.undoAction) {
            Promise.resolve(options.undoAction()).then(() => dismiss())
          }
          break
        case 'custom':
          if (options.action) {
            options.action.onClick()
            dismiss()
          }
          break
      }
    })

    // Auto dismiss
    if (duration) {
      dismissTimer = setTimeout(dismiss, duration)
    }

    // Manage max toasts (BIRHAUS: Cognitive load)
    if (this.toasts.size >= this.maxToasts) {
      const oldestId = this.toasts.keys().next().value
      if (oldestId) {
        const oldestToast = this.toasts.get(oldestId)
        if (oldestToast && oldestToast.parentNode) {
          oldestToast.parentNode.removeChild(oldestToast)
        }
        this.toasts.delete(oldestId)
      }
    }

    container.appendChild(toastElement)
    this.toasts.set(id, toastElement)

    // Animate in
    requestAnimationFrame(() => {
      toastElement.style.transform = 'translateX(0)'
      toastElement.style.opacity = '1'
    })

    return id
  }

  success(message: string, options: Omit<ToastOptions, 'message'> = {}): string {
    return this.createToastElement('success', { 
      messageEs: message, 
      titleEs: options.titleEs || 'Éxito',
      ...options 
    })
  }

  error(message: string, options: Omit<ToastOptions, 'message'> = {}): string {
    return this.createToastElement('error', { 
      messageEs: message, 
      titleEs: options.titleEs || 'Error',
      ...options 
    })
  }

  warning(message: string, options: Omit<ToastOptions, 'message'> = {}): string {
    return this.createToastElement('warning', { 
      messageEs: message, 
      titleEs: options.titleEs || 'Advertencia',
      ...options 
    })
  }

  info(message: string, options: Omit<ToastOptions, 'message'> = {}): string {
    return this.createToastElement('info', { 
      messageEs: message, 
      titleEs: options.titleEs || 'Información',
      ...options 
    })
  }

  dismiss(id?: string): void {
    if (id) {
      const toastElement = this.toasts.get(id)
      if (toastElement) {
        const dismissButton = toastElement.querySelector('[data-action="dismiss"]') as HTMLElement
        dismissButton?.click()
      }
    }
  }

  dismissAll(): void {
    this.toasts.forEach((toastElement) => {
      const dismissButton = toastElement.querySelector('[data-action="dismiss"]') as HTMLElement
      dismissButton?.click()
    })
  }

  // Convenience methods for common use cases
  saved(entity?: string): string {
    const message = entity ? `${entity} guardado exitosamente` : 'Guardado exitosamente'
    return this.success(message)
  }

  deleted(entity?: string): string {
    const message = entity ? `${entity} eliminado exitosamente` : 'Eliminado exitosamente'
    return this.success(message)
  }

  updated(entity?: string): string {
    const message = entity ? `${entity} actualizado exitosamente` : 'Actualizado exitosamente'
    return this.success(message)
  }

  paymentSuccess(amount?: string): string {
    const message = amount ? `Pago de ${amount} procesado exitosamente` : 'Pago procesado exitosamente'
    return this.success(message)
  }
}

// Global instance
export const toast = new BirhausImperativeToast()

// For backward compatibility and flexibility
export default toast